import { JSONCharBlock } from "@reactivated";

import { HotspotWithAlignment } from "./hooks";

// Pixels. Hardcoded for now, we'll see how durable that is
const COLLISION_BUFFER = 36;

/**
 * Check if two rectangles overlap
 */
const rectsOverlap = (rect1: DOMRect, rect2: DOMRect): boolean => {
    /** Pixels. Hardcoded for now, we'll see how durable that is */
    const buffer = COLLISION_BUFFER;

    return !(
        rect1.right + buffer < rect2.left ||
        rect2.right + buffer < rect1.left ||
        rect1.bottom + buffer < rect2.top ||
        rect2.bottom + buffer < rect1.top
    );
};

interface CollisionResult {
    hasCollisions: boolean;
    /** Arrays of indices that collide with each other */
    collisionGroups: number[][];
}

export const detectCollisions = (rects: DOMRect[]): CollisionResult => {
    /** Build list of elements with their original index and rects */
    const elements = rects
        .map((rect, index) => ({ originalIndex: index, rect }))
        .filter((element) => element.rect.width > 0 && element.rect.height > 0);

    /** For each element, find what it collides with and create collision groups */
    const collisionGroups = elements
        .map((element, index) => {
            /**
             * We check each element against only the elements that come after it
             * in the array. This way A-vs-B is checked once (when processing A),
             * not twice (A-vs-B and B-vs-A)
             */
            const colliding = elements
                /** Skip elements 0 through current index, only check what's left */
                .slice(index + 1)
                .filter((other) => rectsOverlap(element.rect, other.rect));

            /**
             * If this element collides with anything, create a group with all
             * colliding elements
             */
            return colliding.length > 0
                ? /** Group = this element + all it collides with */
                  [
                      element.originalIndex,
                      ...colliding.map((e) => e.originalIndex),
                  ]
                : /** No collisions for this element */
                  null;
        })
        /** Remove elements that don't collide with anything */
        .filter((group) => group !== null);

    return {
        hasCollisions: collisionGroups.length > 0,
        collisionGroups,
    };
};

/**
 * Given two hotspots, determine which is the high one and which is the low one
 */
const getVerticalOrder = (group: number[], hotspots: JSONCharBlock) => {
    // CMS prevents > 2 hotspots per side
    if (group.length !== 2) {
        console.warn(
            `Expected collision group of 2 elements, got ${group.length}. Using first 2.`,
        );
    }

    const [indexA, indexB] = group;

    // Get `y` values from hotspot data
    const yA = hotspots[indexA].y;
    const yB = hotspots[indexB].y;

    // Smaller `y` value is closer to the top, so it's 'higher'
    return {
        highIndex: yA < yB ? indexA : indexB,
        lowIndex: yA < yB ? indexB : indexA,
    };
};

const calculateCollisionSeparation = (
    highRect: DOMRect,
    lowRect: DOMRect,
    containerRect: DOMRect,
) => {
    // Calculate total height needed for both elements plus buffer
    const totalHeightNeeded =
        highRect.height + lowRect.height + COLLISION_BUFFER;

    // Find the center point where this total height should be positioned
    const containerHeight = containerRect.height;
    const centerY =
        containerRect.top + (containerHeight - totalHeightNeeded) / 2;

    // Position the higher element at the center point
    const highTranslateY = centerY - highRect.top;

    // Position the lower element below the higher element + buffer
    const lowTranslateY =
        centerY + highRect.height + COLLISION_BUFFER - lowRect.top;

    return { highTranslateY, lowTranslateY };
};

export type ContentLayout = "underLine" | "overLine";

/**
 * Represents the visual adjustments we might want to make to a value prop
 */
export interface ValuePropAdjustment {
    /** CSS will use this to move the prop without changing the DOM layout */
    translateY: number;
    /** Where the line that points to the hotspot appears relative to content */
    contentLayout: ContentLayout;
    /** Hide body copy if it's too tall */
    hideBody: boolean;
    /** Whether this element is colliding with another */
    isColliding: boolean;
}

/**
 * By default, a value prop is not translated vertically, the line is above the
 * content, and it's not hidden
 */
export const baseAdjustment: ValuePropAdjustment = {
    translateY: 0,
    contentLayout: "underLine",
    hideBody: false,
    isColliding: false,
};

export const calculateFinalRect = (
    element: HTMLDivElement,
    adjustment: ValuePropAdjustment,
): DOMRect => {
    const rect = element.getBoundingClientRect();
    let newHeight = rect.height;
    let newTop = rect.top;
    if (adjustment.contentLayout == "overLine") {
        /**
         * Element's content appears over the hotspot line, so subtract
         * the element's height to position it above its normal spot
         */
        newTop = rect.top + adjustment.translateY - rect.height;
    } else {
        /**
         * Element is in normal position: content appears under the hotspot line
         * Apply the collision adjustment (translateY, probably `0`) to move it
         */
        newTop = rect.top + adjustment.translateY;
    }
    if (adjustment.hideBody) {
        /**
         * Element is too tall, so we're going to hide the body
         */
        const body = element.querySelector("p");
        if (body) {
            newHeight = rect.height - body.getBoundingClientRect().height;
        }
    }
    return new DOMRect(rect.left, newTop, rect.width, newHeight);
};

const pairIsTooTall = (
    elementA: HTMLDivElement,
    elementB: HTMLDivElement,
    containerRect: DOMRect,
) => {
    // Get element heights
    const heightA = elementA.getBoundingClientRect().height;
    const heightB = elementB.getBoundingClientRect().height;

    // Calculate combined height with buffer
    const totalHeight = heightA + heightB + COLLISION_BUFFER;

    return totalHeight > containerRect.height;
};

export /**
 * Calculate body hide adjustments for elements that would be too tall combined
 */
const calculateBodyHideAdjustments = (
    _adjustments: ValuePropAdjustment[],
    containerRect: DOMRect,
    refs: React.RefObject<HTMLDivElement[]>,
    hotspots: HotspotWithAlignment[],
): ValuePropAdjustment[] => {
    const adjustments = [..._adjustments];

    const refsWithAlignment = refs.current.map((ref, index) => ({
        ref,
        alignment: hotspots[index].alignment,
        originalIndex: index,
    }));

    const leftRefs = refsWithAlignment.filter(
        (ref) => ref.alignment === "alignLeft",
    );

    // If combined height exceeds container, hide body text for both
    // Should never be more than two but we can just ignore those if they exist
    if (
        leftRefs.length > 1 &&
        pairIsTooTall(leftRefs[0].ref, leftRefs[1].ref, containerRect)
    ) {
        const indexA = leftRefs[0].originalIndex;
        const indexB = leftRefs[1].originalIndex;
        adjustments[indexA] = { ...adjustments[indexA], hideBody: true };
        adjustments[indexB] = { ...adjustments[indexB], hideBody: true };
    }

    // Repeat for the right
    const rightRefs = refsWithAlignment.filter(
        (ref) => ref.alignment === "alignRight",
    );

    if (
        rightRefs.length > 1 &&
        pairIsTooTall(rightRefs[0].ref, rightRefs[1].ref, containerRect)
    ) {
        const indexA = rightRefs[0].originalIndex;
        const indexB = rightRefs[1].originalIndex;
        adjustments[indexA] = { ...adjustments[indexA], hideBody: true };
        adjustments[indexB] = { ...adjustments[indexB], hideBody: true };
    }

    return adjustments;
};

/**
 * Calculate flip adjustments for elements that would breach container bottom
 */
export const calculateFlipAdjustments = (
    adjustments: ValuePropAdjustment[],
    containerRect: DOMRect,
    refs: React.RefObject<HTMLDivElement[]>,
): ValuePropAdjustment[] => {
    return adjustments.map((adjustment, index) => {
        const element = refs.current[index];
        const body = element.querySelector("p");
        const refRect = element.getBoundingClientRect();

        let bottom = refRect.bottom;

        if (adjustment.hideBody && body) {
            bottom = refRect.bottom - body.getBoundingClientRect().height;
        }

        /** Check if this element would extend past the bottom of the container */
        const wouldOverflow = bottom > containerRect.bottom;

        return {
            ...adjustment,
            contentLayout: wouldOverflow ? "overLine" : "underLine",
        };
    });
};

/**
 * Calculate collision adjustments based on flipped positions and collision state
 */
export const calculateCollisionAdjustments = (
    adjustments: ValuePropAdjustment[],
    currentCollisionState: CollisionResult,
    rects: DOMRect[],
    containerRect: DOMRect,
    hotspots: HotspotWithAlignment[],
): ValuePropAdjustment[] => {
    const collisionAdjustments = [...adjustments];

    /** Mark elements as colliding */
    currentCollisionState.collisionGroups.forEach((group) => {
        group.forEach((index) => {
            collisionAdjustments[index] = {
                ...collisionAdjustments[index],
                isColliding: true,
            };
        });
    });

    /** Apply collision separation */
    currentCollisionState.collisionGroups.forEach((group) => {
        const { highIndex, lowIndex } = getVerticalOrder(group, hotspots);

        /** Match our collision pair to the rects we just got */
        const highRect = rects[highIndex];
        const lowRect = rects[lowIndex];

        /** Figure out the new values for the colliding value props */
        const { highTranslateY, lowTranslateY } = calculateCollisionSeparation(
            highRect,
            lowRect,
            containerRect,
        );

        /** Set those values */
        collisionAdjustments[highIndex] = {
            ...collisionAdjustments[highIndex],
            translateY: highTranslateY,
        };
        collisionAdjustments[lowIndex] = {
            ...collisionAdjustments[lowIndex],
            translateY: lowTranslateY,
        };
    });

    return collisionAdjustments;
};
